import { ReactNode } from "react";

import { AppDialog, StatusBadge, StatusTone } from "../../components/ui";
import { formatDateWithoutSeconds } from "../../utils/functions/date";
import {
  BANNER_CTA_TYPE_OPTIONS,
  BANNER_INTERNAL_ROUTE_OPTIONS,
  BANNER_STATUS_OPTIONS,
  Banner,
  BannerStatus,
} from "../../models/banners";
import { BannerLandingPreview } from "./BannerLandingPreview";
import { getBannerDisplayOrder } from "./bannerFormRules";
import "./banners.css";

/**
 * Detalle de solo lectura del banner — SYSTEM_DESIGN §6.3: modal de detalle, un único [Cerrar].
 *
 * Existe por consistencia: en Beneficios, Partners y Categorías la primera acción de la fila es
 * el ojo "Ver", y acá el ojo no estaba (peor: estaba usado para activar/desactivar). Editar abre
 * una página entera, así que esto es el vistazo rápido sin salir del listado.
 *
 * La landing se muestra con el MISMO componente que la vista previa del formulario: el detalle de
 * una Página Interna es justamente cómo se va a ver, no una lista de sus campos.
 */
const STATUS_TONES: Record<BannerStatus, StatusTone> = {
  active: "success",
  inactive: "error",
  scheduled: "pending",
  finished: "info",
};

const statusLabel = (status: BannerStatus): string =>
  BANNER_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;

export const BannerStatusBadge = ({ status }: { status: BannerStatus }) => (
  <StatusBadge label={statusLabel(status)} tone={STATUS_TONES[status]} />
);

const ctaTypeLabel = (banner: Banner): string =>
  BANNER_CTA_TYPE_OPTIONS.find((option) => option.value === banner.ctaType)?.label ?? banner.ctaType;

/** Con `internal_route` el valor guardado es la ruta; se muestra el nombre de la sección. */
const ctaValueLabel = (banner: Banner): string => {
  if (!banner.ctaValue) return "—";
  if (banner.ctaType !== "internal_route") return banner.ctaValue;
  return (
    BANNER_INTERNAL_ROUTE_OPTIONS.find((option) => option.value === banner.ctaValue)?.label ??
    banner.ctaValue
  );
};

/** Mismo bloque label/valor que el detalle de eventos, para que los detalles se lean igual. */
const Fact = ({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) => (
  <div
    className={`flex flex-column${wide ? " event-facts__wide" : ""}`}
    style={{ gap: "var(--space-6)" }}
  >
    <span className="text-xs text-500 font-semibold uppercase">{label}</span>
    <span className="font-semibold text-900">{children}</span>
  </div>
);

interface BannerDetailDialogProps {
  tenantId: string | null;
  /** `undefined` cierra el modal. */
  banner?: Banner;
  onHide: () => void;
}

export const BannerDetailDialog = ({ tenantId, banner, onHide }: BannerDetailDialogProps) => (
  <AppDialog
    visible={!!banner}
    onHide={onHide}
    header={banner?.name ?? "Detalle del banner"}
    variant="detail"
  >
    {banner && (
      <div className="banner-detail">
        {banner.imageUrl && (
          <img className="banner-detail__image" src={banner.imageUrl} alt={banner.name} />
        )}

        {/* Grilla que se adapta al ancho real del modal, no al viewport: ver .event-facts en
            layout.css. Se reusa esa clase en vez de duplicar la misma grilla con otro nombre. */}
        <div className="event-facts">
          <Fact label="Estado">
            <BannerStatusBadge status={banner.status} />
          </Fact>
          <Fact label="Orden">{getBannerDisplayOrder(banner)}</Fact>
          <Fact label="Acción al tocar">{ctaTypeLabel(banner)}</Fact>

          {banner.ctaType !== "internal_page" && (
            <Fact label="Destino" wide>
              {ctaValueLabel(banner)}
            </Fact>
          )}

          {(banner.startDate || banner.endDate || banner.status === "scheduled") && (
            <>
              <Fact label="Inicio">
                {banner.startDate ? formatDateWithoutSeconds(banner.startDate) : "—"}
              </Fact>
              <Fact label="Fin">
                {banner.endDate ? formatDateWithoutSeconds(banner.endDate) : "—"}
              </Fact>
            </>
          )}

          <Fact label="Texto corto" wide>
            {banner.shortText || "—"}
          </Fact>
        </div>

        {tenantId && banner.ctaType === "internal_page" && banner.landingPage && (
          <>
            <hr className="w-full m-0 border-none border-top-1 surface-border" />
            <BannerLandingPreview
              tenantId={tenantId}
              caption="Página interna"
              heroImageUrl={banner.landingPage.heroImageUrl ?? ""}
              title={banner.landingPage.title}
              body={banner.landingPage.body ?? ""}
              callout={banner.landingPage.importantMessage ?? ""}
              buttonEnabled={banner.landingPage.actionButtonEnabled}
              buttonText={banner.landingPage.actionButtonText ?? ""}
            />
          </>
        )}
      </div>
    )}
  </AppDialog>
);
